/**
 * src/components/public/blog/magazine/TableOfContentsMag.tsx
 *
 * Magazine editorial TOC — body H2/H3 başlıklarından dinamik içindekiler.
 *
 * Tasarım:
 *   - "İÇİNDEKİLER" başlık + brand-500 dikey strip (sidebar pattern)
 *   - Numbered list (01, 02, 03...) — büyük slate-300/700 numara solunda
 *   - Aktif madde: brand-600/300 text + brand-500 sol kenar 2px strip
 *   - H3 maddeler ml-9 indent, slate-500 lighter font
 *   - Scroll-spy IntersectionObserver
 *   - Border-b ayraçlar (kart yok)
 *
 * Client Component — useEffect ile DOM observation gerek.
 */

'use client'

import { useEffect, useState } from 'react'

interface TocItem {
  id: string
  text: string
  level: 2 | 3
}

interface TableOfContentsMagProps {
  bodySelector?: string
  label: string
}

export function TableOfContentsMag({
  bodySelector = '#makale-body',
  label,
}: TableOfContentsMagProps) {
  const [items, setItems] = useState<TocItem[]>([])
  const [activeId, setActiveId] = useState<string>('')

  // DOM tarama: heading'leri çek + id'lerini ayarla
  useEffect(() => {
    const body = document.querySelector(bodySelector)
    if (!body) return
    const headings = Array.from(body.querySelectorAll<HTMLElement>('h2, h3'))
    const tocItems: TocItem[] = []
    headings.forEach((h, i) => {
      const level = h.tagName === 'H2' ? 2 : 3
      const text = (h.textContent ?? '').trim()
      if (!text) return
      if (!h.id) {
        const slug = text
          .toLowerCase()
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .replace(/ı/g, 'i')
          .replace(/ş/g, 's')
          .replace(/ğ/g, 'g')
          .replace(/ü/g, 'u')
          .replace(/ö/g, 'o')
          .replace(/ç/g, 'c')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
        h.id = slug || `heading-${i}`
      }
      tocItems.push({ id: h.id, text, level })
    })
    setItems(tocItems)
  }, [bodySelector])

  // Scroll-spy
  useEffect(() => {
    if (items.length === 0) return
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible.length > 0) {
          setActiveId(visible[0]!.target.id)
        }
      },
      { rootMargin: '-80px 0px -70% 0px', threshold: 0 },
    )
    const els = items
      .map((i) => document.getElementById(i.id))
      .filter((el): el is HTMLElement => el !== null)
    els.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [items])

  if (items.length < 2) return null

  // Sadece H2'lere numbera ver, H3'ler indent
  let h2Counter = 0
  const numbered = items.map((item) => {
    if (item.level === 2) h2Counter += 1
    return { ...item, number: item.level === 2 ? h2Counter : null }
  })

  return (
    <nav aria-label={label}>
      <div className="flex items-center gap-3 mb-5">
        <div aria-hidden className="w-1 h-5 bg-brand-500" />
        <h3 className="text-xs uppercase tracking-[0.25em] text-slate-900 dark:text-white font-bold">
          {label}
        </h3>
      </div>
      <ol className="space-y-0 text-sm">
        {numbered.map((item, idx) => {
          const isActive = activeId === item.id
          const isH3 = item.level === 3
          return (
            <li
              key={item.id}
              className={
                idx < numbered.length - 1
                  ? 'border-b border-slate-200 dark:border-slate-800/40'
                  : ''
              }
            >
              <a
                href={`#${item.id}`}
                className={`flex items-start gap-3 py-3 transition-colors ${
                  isH3 ? 'pl-9' : ''
                } ${
                  isActive
                    ? 'text-brand-700 dark:text-brand-300'
                    : isH3
                      ? 'text-slate-500 dark:text-slate-500 hover:text-slate-800 dark:hover:text-slate-200'
                      : 'text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                {item.level === 2 && (
                  <span
                    className={`flex-shrink-0 font-mono text-[11px] font-bold leading-snug tabular-nums ${
                      isActive
                        ? 'text-brand-500 dark:text-brand-400'
                        : 'text-slate-400 dark:text-slate-600'
                    }`}
                  >
                    {String(item.number).padStart(2, '0')}
                  </span>
                )}
                <span
                  className={`flex-1 leading-snug ${
                    isActive ? 'font-semibold' : ''
                  } ${isH3 ? 'text-xs' : ''}`}
                >
                  {item.text}
                </span>
              </a>
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
